import React, { useState, useMemo, useCallback } from 'react';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { FiscalYearBanner } from '../../components/dashboard/FiscalYearBanner';
import { BusinessUnitBanner } from '../../components/dashboard/BusinessUnitBanner';
import { OnboardingBanner } from '../../components/onboarding/OnboardingBanner';
import { RecentActivitiesCard } from '../../components/dashboard/RecentActivitiesCard';
import { WidgetGrid } from '../../components/dashboard/widgets';
import { DashboardChartsView, ChartsPanelData } from '../../components/dashboard/DashboardChartsView';
import { DashboardViewToggle, DashboardViewMode, DashboardViewToggleCompact } from '../../components/dashboard/DashboardViewToggle';
import { useDashboard } from '../../hooks/useDashboard';
import { useDashboardTimeSeries } from '../../hooks/useDashboardTimeSeries';
import { useNotifications } from '../../hooks/useNotifications';
import { useNetworkStatus } from '../../hooks/useProgressiveLoad';

export function DashboardPage() {
  const { dashboardData, loading, refreshDashboard } = useDashboard();
  const { addNotification } = useNotifications();
  const networkStatus = useNetworkStatus();
  
  const [processedAlerts, setProcessedAlerts] = React.useState(new Set<string>());
  const [viewMode, setViewMode] = useState<DashboardViewMode>('cards');
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  // Charger les données time series réelles depuis les APIs
  const { data: timeSeriesData, loading: timeSeriesLoading, refresh: refreshTimeSeries } = useDashboardTimeSeries(chartPeriod);
  
  // Convertir les données du dashboard au format attendu par les widgets
  const widgetData = React.useMemo(() => {
    if (!dashboardData) return null;
    
    return {
      // Financial Position (Bilan)
      financialPosition: {
        balanceSheet: {
          totalAssets: dashboardData.quickStats?.totalAssets ?? dashboardData.financialRatios?.assets ?? 0,
          totalLiabilities: dashboardData.quickStats?.totalLiabilities ?? dashboardData.financialRatios?.liabilities ?? 0,
          totalEquity: dashboardData.quickStats?.totalEquity ?? dashboardData.financialRatios?.equity ?? 0,
          netAssets: (dashboardData.quickStats?.totalAssets ?? 0) - (dashboardData.quickStats?.totalLiabilities ?? 0)
        },
        ratios: {
          currentRatio: dashboardData.financialRatios?.currentRatio ?? 0,
          quickRatio: dashboardData.financialRatios?.quickRatio ?? 0,
          debtEquityRatio: dashboardData.financialRatios?.debtEquityRatio ?? 0,
          debtRatio: dashboardData.financialRatios?.debtRatio ?? 0,
          workingCapital: dashboardData.financialRatios?.workingCapital ?? 0,
          returnOnAssets: dashboardData.financialRatios?.roa ?? 0,
          returnOnEquity: dashboardData.financialRatios?.roe ?? 0
        }
      },
      
      // Profit & Loss (Compte de résultat)
      profitAndLoss: {
        current: {
          revenue: dashboardData.quickStats?.totalRevenue ?? 0,
          expenses: dashboardData.quickStats?.totalExpenses ?? 0,
          grossProfit: dashboardData.quickStats?.grossProfit ?? 0,
          netProfit: dashboardData.quickStats?.netProfit ?? 0,
          profitMargin: dashboardData.quickStats?.profitMargin ?? 0
        },
        comparison: dashboardData.previousPeriod ?? null
      },
      
      // Cash Position (Trésorerie)
      cashPosition: {
        totalCash: dashboardData.quickStats?.cashBalance ?? dashboardData.treasury?.totalCash ?? 0,
        cashOnHand: dashboardData.treasury?.cashOnHand ?? 0,
        bankBalance: dashboardData.treasury?.bankBalance ?? 0,
        cashFlow: {
          inflows: dashboardData.cashFlow?.inflows ?? 0,
          outflows: dashboardData.cashFlow?.outflows ?? 0
        }
      },
      
      // Tax Summary (Fiscalité)
      taxSummary: {
        vatCollected: dashboardData.tax?.vatCollected ?? 0,
        vatPaid: dashboardData.tax?.vatPaid ?? 0,
        vatDue: dashboardData.tax?.vatDue ?? 0,
        corporateTax: dashboardData.tax?.corporateTax ?? 0,
        nextDeadline: dashboardData.tax?.nextDeadline ?? null
      },
      
      // Top Accounts
      topAccounts: dashboardData.topAccounts ?? [],
      
      // Recent Transactions
      recentTransactions: dashboardData.recentTransactions ?? [],
      
      // Metrics (SYSCOHADA)
      metrics: {
        totalAccounts: dashboardData.metrics?.totalAccounts ?? 0,
        totalEntries: dashboardData.metrics?.totalEntries ?? 0,
        totalJournals: dashboardData.metrics?.totalJournals ?? 0,
        lastUpdate: dashboardData.metrics?.lastUpdate ?? new Date().toISOString(),
        classes: dashboardData.metrics?.classes ?? {}
      },
      
      // Key Performance Indicators (Cote de crédit Wanzo)
      keyPerformanceIndicators: {
        creditScore: dashboardData.keyPerformanceIndicators?.creditScore ?? 0,
        financialRating: dashboardData.keyPerformanceIndicators?.financialRating ?? 'N/A'
      }
    };
  }, [dashboardData]);
  
  // Convertir les données pour la vue Charts - UTILISE LES VRAIES DONNÉES DES APIs
  const chartsData = useMemo((): ChartsPanelData | null => {
    // Utiliser les données time series réelles si disponibles
    if (timeSeriesData) {
      return {
        revenueExpenses: timeSeriesData.revenueExpenses.map(item => ({
          date: item.date,
          revenue: item.revenue,
          expenses: item.expenses
        })),
        treasury: timeSeriesData.treasury.map(item => ({
          date: item.date,
          totalCash: item.totalCash,
          cashOnHand: item.totalCash * 0.3, // Approximation si pas fourni
          bankBalance: item.totalCash * 0.7, // Approximation si pas fourni
          inflow: item.inflow,
          outflow: item.outflow,
          netFlow: item.netFlow
        })),
        expenseBreakdown: timeSeriesData.expenseBreakdown.map(item => ({
          name: item.name,
          value: item.value,
          color: item.color,
          percentage: item.percentage
        })),
        summary: timeSeriesData.summary
      };
    }
    
    // Fallback vers les données du dashboard principal si pas de time series
    if (!dashboardData) return null;
    
    // Utiliser revenueData si disponible depuis le dashboard
    const revenueExpenses = (dashboardData.revenueData || []).map((item: any) => ({
      date: item.date || item.month,
      revenue: item.revenue || item.amount || 0,
      expenses: 0 // Sera complété si disponible
    }));
    
    // Répartition des charges depuis expensesData
    const expenseBreakdown = (dashboardData.expensesData || []).map((item: any) => ({
      name: item.name || item.category || 'Autres',
      value: item.value || item.amount || 0,
      color: item.color || '#94a3b8',
      percentage: 0
    }));
    
    // Calculer les pourcentages
    const totalExpenses = expenseBreakdown.reduce((sum: number, item: any) => sum + item.value, 0);
    if (totalExpenses > 0) {
      expenseBreakdown.forEach((item: any) => {
        item.percentage = (item.value / totalExpenses) * 100;
      });
    }
    
    // Trésorerie depuis les données du dashboard
    const treasury = [{
      date: new Date().toISOString().split('T')[0],
      totalCash: dashboardData.quickStats?.cashOnHand ?? 0,
      cashOnHand: (dashboardData as any).treasury?.cashOnHand ?? 0,
      bankBalance: (dashboardData as any).treasury?.bankBalance ?? 0,
      inflow: (dashboardData as any).cashFlow?.inflows ?? 0,
      outflow: (dashboardData as any).cashFlow?.outflows ?? 0,
      netFlow: ((dashboardData as any).cashFlow?.inflows ?? 0) - ((dashboardData as any).cashFlow?.outflows ?? 0)
    }];
    
    return {
      revenueExpenses,
      treasury,
      expenseBreakdown,
      summary: {
        totalRevenue: dashboardData.quickStats?.revenue ?? 0,
        totalExpenses: totalExpenses,
        netProfit: dashboardData.quickStats?.netIncome ?? 0,
        profitMargin: 0,
        totalCash: dashboardData.quickStats?.cashOnHand ?? 0,
        cashChange: 0
      }
    };
  }, [timeSeriesData, dashboardData]);
  
  // Fonction pour rafraîchir les données du dashboard
  const handleRefresh = useCallback(() => {
    refreshDashboard();
    refreshTimeSeries();
    addNotification({
      title: 'Tableau de bord',
      message: 'Les données ont été rafraîchies avec succès',
      type: 'success'
    });
  }, [refreshDashboard, refreshTimeSeries, addNotification]);
  
  // Convertir les alertes en notifications
  React.useEffect(() => {
    const alerts = dashboardData?.alerts;
    if (alerts && alerts.length > 0) {
      alerts.forEach((alert: any) => {
        const alertId = `${alert.type}_${alert.message?.substring(0, 50) ?? ''}`;
        
        if (!processedAlerts.has(alertId)) {
          setProcessedAlerts(prev => new Set(prev).add(alertId));
          addNotification({
            title: 'Alerte',
            message: alert.message,
            type: alert.type === 'success' ? 'success' : 
                  alert.type === 'warning' ? 'warning' : 'error'
          });
        }
      });
    }
  }, [dashboardData?.alerts]);

  return (
    <div className="space-y-6 pb-8">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Tableau de Bord
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vue d'ensemble de votre comptabilité
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Toggle vue Cards/Charts - Desktop */}
          <div className="hidden sm:block">
            <DashboardViewToggle 
              activeView={viewMode} 
              onViewChange={setViewMode} 
            />
          </div>
          
          {/* Toggle vue Cards/Charts - Mobile */}
          <div className="sm:hidden">
            <DashboardViewToggleCompact 
              activeView={viewMode} 
              onViewChange={setViewMode} 
            />
          </div>
          
          {/* Indicateur de qualité réseau */}
          <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full transition-colors ${
            networkStatus.quality === 'excellent' || networkStatus.quality === 'good'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : networkStatus.quality === 'moderate'
              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              : networkStatus.quality === 'offline'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
          }`}>
            {networkStatus.isOnline ? (
              <Wifi className="h-3.5 w-3.5" />
            ) : (
              <WifiOff className="h-3.5 w-3.5" />
            )}
            <span className="hidden sm:inline capitalize">{networkStatus.quality}</span>
          </div>
          
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || timeSeriesLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || timeSeriesLoading) ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualiser</span>
          </Button>
        </div>
      </div>

      {/* Bannières importantes */}
      <div className="space-y-3">
        <FiscalYearBanner />
        <BusinessUnitBanner />
        <OnboardingBanner />
      </div>
      
      {/* Vue Widgets (Cards) - Style Notion */}
      {viewMode === 'cards' && (
        <WidgetGrid 
          data={widgetData} 
          isLoading={loading || timeSeriesLoading} 
        />
      )}
      
      {/* Vue Graphiques - Style QuickBooks/Sage */}
      {viewMode === 'charts' && chartsData && (
        <DashboardChartsView 
          data={chartsData}
          isLoading={loading || timeSeriesLoading}
          onRefresh={handleRefresh}
          onPeriodChange={setChartPeriod}
        />
      )}
      
      {/* Message si pas de données pour les graphiques */}
      {viewMode === 'charts' && !chartsData && !(loading || timeSeriesLoading) && (
        <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aucune donnée disponible
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Les graphiques seront affichés une fois les données chargées.
            </p>
            <Button
              variant="outline"
              onClick={handleRefresh}
              className="mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>
      )}

      {/* Activités récentes - Conservé comme demandé */}
      <div className="mt-8">
        <RecentActivitiesCard />
      </div>
    </div>
  );
}